import { useMemo, useState } from 'react'
import type { AgentFinalTag, AgentRunDetail, AgentSubmission } from '~/entities/document-agent'
import { cn } from '~/shared/lib/cn'
import { pluralize } from '~/shared/lib/pluralize'
import { type CodeRow, type CodeStatus, describeCode, type RunReview } from '../lib'
import { EvidenceQuote, MaterialIcon, Muted, WithTip } from './primitives'

const STATUS: Record<CodeStatus, { label: string; className: string; dot: string }> = {
  agreed: { label: 'Agreed', className: 'text-success-content', dot: 'bg-alert-success-message' },
  missed: {
    label: 'Missed by the agent',
    className: 'text-alert-error-content',
    dot: 'bg-alert-error-message',
  },
  extra: {
    label: 'Added by the agent',
    className: 'text-alert-error-content',
    dot: 'bg-alert-error-message',
  },
  unscored: {
    label: 'Not reviewed',
    className: 'text-text-secondary',
    dot: 'bg-content-low-emphasis',
  },
}

function rowStatus(row: CodeRow): { label: string; className: string; dot: string } {
  if (row.agent === 'agreed' && !row.inFinal && !row.inAgent) {
    // Only production has it, and the final state does not: the agent was right to leave it out.
    return {
      label: 'Production only',
      className: 'text-text-secondary',
      dot: 'bg-content-low-emphasis',
    }
  }
  return STATUS[row.agent]
}

function Presence({ present, title }: { present: boolean; title: string }) {
  return (
    <WithTip tip={`${title}: ${present ? 'has this code' : 'does not have this code'}`}>
      <span className="flex justify-center">
        {present ? (
          <span className="bg-text-primary size-2.5 rounded-full" />
        ) : (
          <span className="border-content-low-emphasis size-2.5 rounded-full border" />
        )}
      </span>
    </WithTip>
  )
}

const GRID = 'grid grid-cols-[88px_minmax(0,1fr)_56px_56px_84px_156px_20px] items-center gap-x-3'

function submittedCodes(
  submission: AgentSubmission | null,
): Map<string, NonNullable<AgentSubmission['carc_codes']>[number]> {
  const all = [
    ...(submission?.carc_codes ?? []),
    ...(submission?.rarc_codes ?? []),
    ...(submission?.natera_tags ?? []),
  ]
  return new Map(all.map((item) => [item.code, item]))
}

function CodeDetail({
  row,
  tag,
  submitted,
}: {
  row: CodeRow
  tag: AgentFinalTag | undefined
  submitted: ReturnType<typeof submittedCodes>
}) {
  const own = submitted.get(row.code)
  const quote = tag?.evidence ?? own?.evidence_quote
  const reasoning = tag?.explanation ?? own?.reasoning
  const confidence = tag?.confidence ?? own?.confidence
  const timeframe = tag?.timeframe_days ?? own?.timeframe_days

  if (!row.inAgent) {
    return (
      <Muted>
        {own
          ? "The agent submitted this code, but today's tag rules removed it from its result."
          : 'The agent did not submit this code.'}
      </Muted>
    )
  }
  if (!own && !quote) {
    return (
      <Muted>
        {"Added by today's tag rules on top of the agent's codes, so it has no evidence quote."}
      </Muted>
    )
  }
  return (
    <div className="flex flex-col gap-2">
      {quote && <EvidenceQuote text={quote} />}
      {reasoning && (
        <p className="text-text-primary m-0 text-[13px] leading-5 whitespace-pre-wrap">
          {reasoning}
        </p>
      )}
      <div className="flex flex-wrap gap-x-4">
        {confidence && <Muted>{`Confidence: ${confidence}`}</Muted>}
        {typeof timeframe === 'number' && <Muted>{`Timeframe: ${timeframe} days`}</Muted>}
      </div>
    </div>
  )
}

/** Every code any source has, with what it means and the agent's evidence for it. */
export function CodesBreakdown({ run, review }: { run: AgentRunDetail; review: RunReview }) {
  const { rows, hasFinal } = review.codes
  const tags = useMemo(
    () => new Map((run.final?.tags?.tags ?? []).map((tag) => [tag.code, tag])),
    [run.final?.tags?.tags],
  )
  const submitted = useMemo(
    () => submittedCodes((run.result?.submission ?? null) as AgentSubmission | null),
    [run.result?.submission],
  )
  const [open, setOpen] = useState<Set<string>>(
    () =>
      new Set(
        rows
          .filter((row) => row.agent === 'missed' || row.agent === 'extra')
          .map((row) => row.code),
      ),
  )

  if (rows.length === 0) return null

  const toggle = (code: string) =>
    setOpen((current) => {
      const next = new Set(current)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })

  return (
    <div
      id="codes"
      className="border-divider scroll-mt-4 border-t px-4 pt-3 pb-2"
    >
      <div className="flex items-baseline justify-between gap-4 pb-2">
        <span className="text-text-primary text-[13px] leading-5 font-semibold">
          {`Code by code (${rows.length} ${pluralize(rows.length, 'code')})`}
        </span>
        <Muted>Open a code to see the agent&apos;s evidence for it</Muted>
      </div>
      <div
        className={cn(
          GRID,
          'text-text-tertiary border-divider border-b pb-1.5 text-[11px] leading-4 font-medium tracking-[0.4px] uppercase',
        )}
      >
        <span>Code</span>
        <span>Meaning</span>
        <span className="text-center">Final</span>
        <span className="text-center">Agent</span>
        <span className="text-center">Production</span>
        <span>Agent vs final</span>
        <span />
      </div>
      <ul className="m-0 list-none p-0">
        {rows.map((row) => {
          const meaning = describeCode(row.code)
          const description = tags.get(row.code)?.description || meaning.description
          const status = hasFinal ? rowStatus(row) : STATUS.unscored
          const isOpen = open.has(row.code)
          return (
            <li
              key={row.code}
              className="border-divider border-b last:border-b-0"
            >
              <button
                type="button"
                onClick={() => toggle(row.code)}
                className={cn(
                  GRID,
                  'hover:bg-surface-low w-full cursor-pointer border-none bg-transparent py-2 text-left',
                )}
                aria-expanded={isOpen}
              >
                <span className="font-mono text-[13px] leading-5 font-medium">{row.code}</span>
                <span className="flex min-w-0 flex-col">
                  <span className="text-text-primary line-clamp-2 text-[13px] leading-5">
                    {description ?? 'No description'}
                  </span>
                  {meaning.kind && (
                    <span className="text-text-tertiary text-[11px] leading-4">{meaning.kind}</span>
                  )}
                </span>
                <Presence
                  present={row.inFinal}
                  title="Final"
                />
                <Presence
                  present={row.inAgent}
                  title="Agent"
                />
                <Presence
                  present={row.inToday}
                  title="Production"
                />
                <span
                  className={cn(
                    'flex items-center gap-1.5 text-[12px] leading-4 font-medium',
                    status.className,
                  )}
                >
                  <span className={cn('size-2 rounded-full', status.dot)} />
                  {status.label}
                </span>
                <MaterialIcon
                  name={isOpen ? 'expand_less' : 'expand_more'}
                  className="text-text-tertiary text-[18px]"
                />
              </button>
              {isOpen && (
                <div className="flex flex-col gap-2 pt-1 pb-3 pl-[100px]">
                  {description && description.length > 110 && (
                    <p className="text-text-secondary m-0 text-[12px] leading-4">{description}</p>
                  )}
                  <CodeDetail
                    row={row}
                    tag={tags.get(row.code)}
                    submitted={submitted}
                  />
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
